import api from './api';
import api2 from './api-v2';

const filter = async ({ filters }) => {
    const client = await api.getClient();
    return await api.handleSuccess(await client.GetClusters({ filters: JSON.stringify(filters) }));
};

const filter2 = async (filters) => {
    const client = await api2.getClient();
    const clusters = await client.get('clusters', { params: filters });
    clusters.data.items.map(convertClusterObjects);
    return api.handleSuccess(clusters);
};

// filter3 / getById3 read clusters from fleet's /api/v3 (clusters-fleet flag
// cutover). The v3 responses are shaped to core2's v2 contract server-side
// ({items, meta} envelope, bare detail record, parsed updates JSON), so these
// mirror filter2/getById2 with the v3 client.
const filter3 = async (filters) => {
    const client = await api2.getClientV3();
    const clusters = await client.get('clusters', { params: filters });
    clusters.data.items.map(convertClusterObjects);
    return api.handleSuccess(clusters);
};

// healthSummary3 reads fleet's /api/v3/cluster-health-summary: a GROUP BY
// count rollup (computedHealthState buckets + onlineStatus buckets) across
// every cluster visible to the org, scoped identically to filter3 - used by
// Dashboard.vue's Health Status chart instead of paging through every
// cluster record just to produce counts. Kept as its own top-level path
// segment (not nested under clusters/, e.g. clusters/health-summary) to
// avoid sitting as a literal sibling of clusters/{id}'s wildcard route at
// the gateway - see the RPC's own doc on the Go side for why.
const healthSummary3 = async () => {
    const client = await api2.getClientV3();
    return await api.handleSuccess(await client.get('cluster-health-summary'));
};

// connectivityHistory3 reads fleet's /api/v3/cluster-connectivity-history:
// one point per bucket over [from, to] with how many of the org's clusters
// were online vs disconnected (a deleted one up to its deletion), plus the
// fleet size now. Scoped by the same visibility rule as healthSummary3, so the
// Connectivity and Health cards on the dashboard agree on the fleet today.
// from/to are ISO strings resolved client-side. Same top-level path segment
// reasoning as healthSummary3.
const connectivityHistory3 = async ({ from, to }) => {
    const client = await api2.getClientV3();
    return await api.handleSuccess(
        await client.get('cluster-connectivity-history', { params: { from, to } }),
    );
};

const getById3 = async ({ id }) => {
    const client = await api2.getClientV3();
    const cluster = await client.get(`clusters/${id}`);
    convertClusterObjects(cluster.data);
    return api.handleSuccess(cluster);
};

// listNodes3 reads the cluster-detail Nodes table rows from fleet
// (/api/v3/clusters/{id}/nodes, #4919): every roster member as a node — the
// same `node` object the hardware list embeds (#5043), plus a `hardware`
// {id, serialNumber} back-reference. Unpaginated; returns the items array.
const listNodes3 = async ({ id }) => {
    const client = await api2.getClientV3();
    const response = await client.get(`clusters/${id}/nodes`);
    const { data } = await api.handleSuccess(response);
    return data?.items || [];
};

// listConditions3 reads the cluster-detail Conditions panel rows from fleet
// (/api/v3/clusters/{id}/conditions, #5059): every currently-active
// ClusterCondition row, with kind/type/label already derived server-side (no
// client-side catalog lookup needed - see ClusterConditionV3's doc on the Go
// side). Unpaginated; returns the items array.
const listConditions3 = async ({ id }) => {
    const client = await api2.getClientV3();
    const response = await client.get(`clusters/${id}/conditions`);
    const { data } = await api.handleSuccess(response);
    return data?.items || [];
};

// Node ids for the per-node metric charts; from the v3 roster with clusters-fleet (#5072).
const listNodeUuids = async ({ id, fleet }) => {
    if (fleet) return (await listNodes3({ id })).map((row) => row.hcosIdentifier);
    const response = await getById({ id });
    return (response?.data?.data?.cluster?.data?.meta?.nodes || []).map((n) => n.uuid);
};

// nodeSnapshotToRow maps a fleet node's inventory/usage fields (flat on the
// node, named like the cluster's cpuPercent/memoryPercent, #5043) onto the
// v1 collector row shape the components render — the one place the API
// field names meet the template field names. 64-bit byte counts transcode as
// JSON strings; the templates divide them.
const nodeSnapshotToRow = (node) => {
    const bytes = (v) => (v == null ? undefined : Number(v));
    const n = node || {};
    return {
        driveCount: n.driveCount,
        numCPUs: n.cpuCount,
        numCores: n.coreCount,
        cpuUsage: n.cpuPercent,
        memAllocationPercentage: n.memoryPercent,
        capacity: bytes(n.capacityBytes),
        capacityUsage: bytes(n.capacityUsedBytes),
        supportTunnel: { tunnel: n.supportTunnel },
        statsAt: n.statsAt,
    };
};

const getById = async ({ id }) => {
    const client = await api.getClient();
    return await api.handleSuccess(await client.GetCluster({ clusterId: id }));
};

const getById2 = async ({ id }) => {
    const client = await api2.getClient();
    const cluster = await client.get(`clusters/${id}`);
    convertClusterObjects(cluster.data);
    return api.handleSuccess(cluster);
};

const removeById = async ({ id }) => {
    const client = await api.getClient();
    return await api.handleSuccess(await client.DeleteCluster({ clusterId: id }));
};

// removeById3 removes a cluster through fleet (DELETE /api/v3/clusters/{id},
// #5134) under clusters-fleet. data.evicted is true when this org doesn't own
// the cluster, so it was only removed from this organization's view.
const removeById3 = async ({ id }) => {
    const client = await api2.getClientV3();
    return api.handleSuccess(await client.delete(`clusters/${id}`));
};

const commandSend = async ({ id, command, payload }) => {
    const client = await api.getClient();
    return await api.handleSuccess(await client.ClusterCommandSend({ clusterId: id }, { command, payload }));
};

const create = async ({ payload }) => {
    const client = await api.getClient();
    return await api.handleSuccess(await client.CreateCluster(null, payload));
};

const upsert = async ({ id, payload }) => {
    const client = await api2.getClient();
    return await api2.handleSuccess(await client.post(`clusters/${id}/tags`, payload));
};

const updateTags = async ({ id, tags }) => {
    const client = await api2.getClient();
    return await api2.handleSuccess(await client.post(`clusters/${id}/tags`, { tags: tags }));
};

const getConfig = async ({ clusterId }) => {
    const client = await api.getClient();
    return api.handleSuccess(await client.GetClusterConfig({ clusterId: clusterId }));
};

const startTunnel = async ({ clusterId, additionalRouteArgs, useUuid, force }) => {
    const client = await api.getClient();
    return api.handleSuccess(await client.StartTunnel({ clusterId: clusterId }, { additionalRouteArgs, useUuid, force }));
};

// getTunnelCluster3 / startTunnel3 are Secure Link's clusters-fleet path
// (#5132). Both map fleet's response onto the v1 shapes ClusterTunnel.vue
// reads, so the page logic stays shared. Unset tunnel capability = no
// compatibilities reported; unset tunnel = never reported. Pre-9.2.0 clusters
// report capability 1 so the page redirects to the node — fleet rejects them.
const belowTunnelVersion = (version) => {
    const [major, minor] = (version || '').split('.').map((p) => parseInt(p, 10));
    return major < 9 || (major === 9 && minor < 2);
};

const tunnelClusterFromV3 = (c) => ({
    onlineStatus: c.onlineStatus,
    data: {
        name: c.name,
        version: c.version,
        compatibilities: c.tunnelCapabilityVersion == null ? {} : {
            tunnel: { version: belowTunnelVersion(c.version) ? 1 : c.tunnelCapabilityVersion },
        },
        meta: {
            tunnel: c.tunnel,
            nodes: c.leaderNodeLanIp ? [{ lanIP: c.leaderNodeLanIp }] : [],
        },
        updates: { status: c.updatesStatus },
    },
});

const getTunnelCluster3 = async ({ id }) => {
    const client = await api2.getClientV3();
    const { data } = await api.handleSuccess(await client.get(`clusters/${id}`));
    return { data: data && tunnelClusterFromV3(data) };
};

// Fleet rejects failures (400 when the cluster can't tunnel), so a response
// is always v1's isValid envelope.
const startTunnel3 = async ({ clusterId, additionalRouteArgs, force }) => {
    const client = await api2.getClientV3();
    const { data } = await api.handleSuccess(await client.post(`clusters/${clusterId}/tunnel`, { additionalRouteArgs, force }));
    return { data: data && { isValid: true, data: { url: data.url } } };
};

// commandSend3 is commandSend's clusters-fleet path (#5133): fleet relays the
// command to the leader via node-request and answers {} once accepted.
const commandSend3 = async ({ id, command, payload }) => {
    const client = await api2.getClientV3();
    return api.handleSuccess(await client.post(`clusters/${id}/command`, { command, payload }));
};

const updateCluster = async ({ clusterId, update }) => {
    const client = await api.getClient();
    return api.handleSuccess(await client.UpdateCluster({ clusterId }, update));
};

const convertClusterObjects = (e) => {
    if (e.tags) {
        e.tags = e.tags.split(',');
    }
    return e;
};

const evict = async({ clusterId }) => {
    const client = await api2.getClient();
    return await api.handleSuccess(await client.post(`clusters/${clusterId}/evict`));
}

const download = async (filters) => {
    const client = await api2.getClient();
    return await client.get('clusters/download', { params: filters, responseType: 'stream' });
};

// download3 reads the CSV from fleet's /api/v3/clusters/download (#5099). No
// params forwarded: SCTable hands the download function UI-only fields (e.g.
// `columns`) that fleet would reject, and the export is unfiltered either way.
const download3 = async () => {
    const client = await api2.getClientV3();
    return await client.get('clusters/download', { responseType: 'blob' });
};

export default {
    filter,
    getById,
    getById2,
    removeById,
    removeById3,
    commandSend,
    commandSend3,
    create,
    getConfig,
    startTunnel,
    getTunnelCluster3,
    startTunnel3,
    updateCluster,
    filter2,
    filter3,
    healthSummary3,
    connectivityHistory3,
    getById3,
    listNodes3,
    listConditions3,
    listNodeUuids,
    nodeSnapshotToRow,
    upsert,
    updateTags,
    evict,
    download,
    download3,
};
